import { useEffect, useRef, useState, useCallback } from 'react';

export const useResettableTimeout = (
  callback: () => void,
  delay: number,
): (() => void) => {
  const savedCallback = useRef<() => void>();
  const [toggle, setToggle] = useState(false);

  useEffect(() => {
    savedCallback.current = callback;
  });

  const resetTimeout = useCallback(() => {
    setToggle(val => !val);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      savedCallback.current();
    }, delay);

    return () => clearTimeout(timer);
  }, [toggle, delay]);

  return resetTimeout;
};

const useTimeout = (callback: () => void, delay: number): void => {
  useResettableTimeout(callback, delay);
};

export const useImperativeTimer = (
  callback: () => void,
  delay: number,
  allowZero = false,
): { stop: () => void; restart: () => void } => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  const restart = () => {
    clearTimeout(timerRef.current);
    if (delay > 0 || allowZero) {
      timerRef.current = setTimeout(() => {
        callback();
      }, delay);
    }
  };

  const stop = () => {
    clearTimeout(timerRef.current);
  };

  return {
    stop,
    restart,
  };
};

export default useTimeout;